import type { AnyRoute, ParseRoute } from "@tanstack/react-router";

import type { routeTree } from "../../routeTree.gen";
import { KebabToCamel } from "../types/type-helpers";

type RouteIds<TRouteTree extends AnyRoute> = ParseRoute<TRouteTree>["path"];

type WithLeadingSlash<T extends string> = T extends `/${string}` ? T : `/${T}`;

export type Pathname = WithLeadingSlash<
  Exclude<RouteIds<typeof routeTree>, "">
>;

type LastSegmentOfPath<T> = T extends "/"
  ? T
  : T extends `${infer LastSegment}/`
    ? LastSegment
    : T extends `${string}/$${infer LastSegment}`
      ? LastSegment
      : T extends `/${infer LastSegment}/${infer Action}`
        ? `${LastSegment}${Capitalize<Action>}`
        : T;

type PathWithoutSlash<T> = T extends "/"
  ? "index"
  : T extends `/${infer P}${"/" | ""}`
    ? P
    : T;

type WithoutLastSlash<T> = T extends "/"
  ? T
  : T extends `/${infer U}/`
    ? `/${U}`
    : T;

type GeneratedRoutes = {
  [K in Pathname as KebabToCamel<
    PathWithoutSlash<LastSegmentOfPath<K>>
  >]: WithoutLastSlash<K>;
};

export type Routes = GeneratedRoutes;

export const ROUTES: Routes = {
  index: "/",
  charges: "/charges",
  deals: "/deals",
  login: "/login",
  meterReadings: "/meter-readings",

  chargeId: "/charges/$chargeId",
  rentAreaId: "/deals/$rentAreaId",
  news: "/news",
  newsId: "/news/$newsId",
  orders: "/orders",
  ordersCreate: "/orders/create",
  orderId: "/orders/$orderId",
  profile: "/profile",
};

export type Route = Routes[keyof Routes];

export const getOrderRoute = (orderId: string) =>
  ROUTES.orderId.replace("$orderId", encodeURIComponent(orderId));

export const getDealRoute = (rentAreaId: string) =>
  ROUTES.rentAreaId.replace("$rentAreaId", encodeURIComponent(rentAreaId));

export const getChargeRoute = (chargeId: string) =>
  ROUTES.chargeId.replace("$chargeId", encodeURIComponent(chargeId));

export const getNewsRoute = (newsId: string) =>
  ROUTES.newsId.replace("$newsId", encodeURIComponent(newsId));
